import {
  BuildSimpleExtractedFieldDto,
  BuildSimpleExtractedTableRowDto,
} from '../models/build-simple-model';
import { compareBy, entries } from '@ps-gui/shared-utils';
import { getRowLowerOffset } from './build-simple-row-access';

type RowDataMergeStrategy = 'concatenate' | 'highest-confidence';

/**
 * TODO: Decide with PO / PM what should happen. We implemented two possible solutions for now.
 */
const ACTIVE_ROW_DATA_MERGE_STRATEGY: RowDataMergeStrategy = 'highest-confidence';

/**
 * The distance which is used to decide if two rows are "too close to each other" such that they
 * have to be merged. We have to experiment to find a good value.
 *
 * The problem with a fixed value is that it is interpreted as % in the respective dimension (here y).
 * For example, a rotated A4 page results in a different absolute threshold distance as the
 * unrotated version, which is nonsensical.
 */
export const ROW_MERGE_DISTANCE_THRESHOLD = 0.01;

/**
 * Note that, similar to most of the logic in build-simple-table-processing.ts, this only
 * makes sense for rectangular "usual shaped" tables.
 * But build simple does not extract "usual shaped" tables by default - a "table" result is just a
 * set of fields collected into "groups".
 */
export const mergeRowsTooCloseToEachOther = (
  rows: BuildSimpleExtractedTableRowDto[],
): BuildSimpleExtractedTableRowDto[] => {
  const rowsAndOffsets = rows
    .map((row) => {
      const lowerOffset = getRowLowerOffset(row);
      return lowerOffset === undefined ? undefined : ([row, lowerOffset] as const);
    })
    .filter((rowAndOffset) => rowAndOffset !== undefined)
    .toSorted(compareBy(([_, offset]) => offset));

  const filteredRows: BuildSimpleExtractedTableRowDto[] = [];
  let lastRowOffset = 0;

  for (const [currentRow, currentOffset] of rowsAndOffsets) {
    if (filteredRows.length === 0 || currentOffset - lastRowOffset > ROW_MERGE_DISTANCE_THRESHOLD) {
      filteredRows.push(currentRow);
      lastRowOffset = currentOffset;
      continue;
    }

    // this access is ok because we don't get here when there is no row in filteredRows yet
    const mergedRow = mergeRows(filteredRows[filteredRows.length - 1], currentRow);
    filteredRows[filteredRows.length - 1] = mergedRow;
    // merging two positioned rows always keeps at least one located member
    lastRowOffset = getRowLowerOffset(mergedRow)!;
  }

  // A row without any location cannot be compared by distance and cannot contribute a table line,
  // so it passes through unmerged instead of taking the whole batch load down.
  return [...filteredRows, ...rows.filter((row) => getRowLowerOffset(row) === undefined)];
};

const mergeRows = (
  row1: BuildSimpleExtractedTableRowDto,
  row2: BuildSimpleExtractedTableRowDto,
): BuildSimpleExtractedTableRowDto => {
  const mergedFields = { ...row1.members };

  for (const [key, field2] of entries(row2.members)) {
    const field1 = mergedFields[key];

    // Only two located fields can be compared by confidence. A field without a location is still
    // kept as a value, it just loses against a located one.
    if (field1?.location === undefined) {
      mergedFields[key] = field2 ?? field1;
    } else if (field2?.location !== undefined) {
      mergedFields[key] = mergeFields(field1, field2, ACTIVE_ROW_DATA_MERGE_STRATEGY);
    }
  }

  return { members: mergedFields };
};

const mergeFields = (
  field1: BuildSimpleExtractedFieldDto,
  field2: BuildSimpleExtractedFieldDto,
  strategy: RowDataMergeStrategy,
) => {
  if (strategy === 'concatenate') {
    return {
      ...field1,
      value: `${field1.value} ${field2.value}`,
    };
  } else if (strategy === 'highest-confidence') {
    return field1.confidence >= field2.confidence ? field1 : field2;
  } else {
    throw new Error(`Unknown row data merge strategy: ${strategy}`);
  }
};
